<template>
  <AppWithLoading v-slot:default="{loader}">
    <template v-if="isTrialTry && plan">
      <div class="trial-text">
        <div class="trial-text-title">
          {{ $t('trialTextTitle', { plan: plan.title }) }}
        </div>
        <div class="trial-text-subtitle">
          {{ $t('trialTextSubtitle.1', {
            plan: plan.title,
            price: plan.price.value,
            currency: plan.price.currency
          }) }}
          <br>
          <span>
            {{ $t('trialTextSubtitle.2') }}
          </span>
          <span>
            {{ $t('trialTextSubtitle.3', { price: formatCurrencyPrice }) }}
          </span>
        </div>
      </div>
    </template>
    <StripePaymentCard :key="date">
      <template v-slot:validator="{validate}">
        <div v-if="error">
          <v-alert
            type="error"
          >
            {{ error }}
          </v-alert>
          <v-alert type="warning" v-if="locale === 'ru'">
            Сейчас наблюдаются проблемы с платежами от некоторых российских банков. При возникновении трудностей с оплатой обращайтесь в <a target="_blank" :href="`mailto:${supportEmail}?subject=Проблема с картой`">техподдержку</a>.
          </v-alert>
        </div>
        <slot />
        <VBtn :disabled="!inputCompleted" @click="loader(()=>validate() && save())">
          {{ $t('add') }}
        </VBtn>
      </template>
    </StripePaymentCard>
  </AppWithLoading>
</template>

<script>
import usePaymentMethods from '@/compositions/accounting/usePaymentMethods';
import StripePaymentCard from '@/components/Payment/Payment/Stripe/StripePaymentCard';
import useStripe from '@/compositions/accounting/useStripe';
import AppWithLoading from '@/components/AppWithLoading';
import { computed, ref, toRefs } from '@vue/composition-api';
import { useI18n } from 'vue-i18n-composable';
import useAccount from '@/compositions/accounting/useAccount';
import usePlan from '@/compositions/accounting/usePlan';
import useNumberFormatter from '@/compositions/useNumberFormatter';
import usePeriod from '@/compositions/accounting/usePeriod';
import {
  paymentTabs,
  settingsTabs,
} from '@/enums';
import { SUPPORT_EMAIL } from '@/helpers/constants';

export default {
  name: 'PaymentMethodCardStripeAdd',
  components: { AppWithLoading, StripePaymentCard },
  props: {
    dialogPaymentStripe: {
      type: Boolean,
      required: false,
      default: true,
    },
    trialPage: {
      type: Boolean,
      required: false,
      default: false,
    },
    isTrialTry: {
      type: Boolean,
      required: false,
      default: false,
    },
    currentTrialPlanId: {
      type: Number,
      required: false,
      default: null,
    },
  },
  setup(props, { emit, root: { $router, $route } }) {
    const { currentTrialPlanId, isTrialTry } = toRefs(props);
    const error = ref(null);
    const date = ref(Date.now());
    const { fetchAll } = usePaymentMethods();
    const { fetchAll: fetchPlans, plans } = usePlan();
    const { fetch } = useAccount();
    const { formatCurrencyFromMoney } = useNumberFormatter();
    const { createPaymentMethod, inputCompleted, createPaymentMethodWithTrial } = useStripe({
      onSuccess() {
        error.value = null;
        date.value = Date.now();
        emit('created');
      },
      onFailure(errorData) {
        error.value = errorData.message;
      },
    });

    const plan = computed(() => plans.value.find((p) => p.id === currentTrialPlanId.value));

    const calculatedPrice = computed(() => {
      const { maxDiscountPeriod } = usePeriod();
      if (!plan.value.price || !maxDiscountPeriod.value) {
        return {};
      }
      return {
        ...plan.value.price,
        withDiscount: plan.value.price.value * ((100 - maxDiscountPeriod.value.discountPercent) / 100),
      };
    });

    const currency = computed(() => plan.value.price.currency);

    const formatCurrencyPrice = computed(() => formatCurrencyFromMoney(
      {
        value: calculatedPrice.value.withDiscount,
        currency: currency.value,
      },
    ));

    const { locale } = useI18n();

    async function saveWithTrial() {
      await createPaymentMethodWithTrial($route.params.planId);
      emit('closeDialogStripe');
      await fetchPlans();
      await $router.push({
        name: 'settings',
        params: {
          type: settingsTabs.PAYMENT,
          addType: paymentTabs.PLAN,
        },
      });
    }

    async function save() {
      if (isTrialTry.value) {
        await saveWithTrial();
        await fetch();
        return;
      }

      await createPaymentMethod();
      await fetchAll();
    }

    return {
      save,
      error,
      inputCompleted,
      date,
      saveWithTrial,
      locale: computed(() => locale.value.toLowerCase()),
      plan,
      formatCurrencyPrice,
      supportEmail: SUPPORT_EMAIL,
    };
  },
};
</script>

<style lang="scss" scoped>
.trial-text {
  margin-bottom: 30px;
  max-width: 80%;
  @media screen and (max-width: 599px) {
    max-width: 100%;
  }
}
.trial-text-title {
  font-size: 24px;
  font-weight: 600;
  line-height: 120%;
  margin-bottom: 15px;
  color: #212121;
}

.trial-text-subtitle {
  font-size: 16px;
  line-height: 120%;
  color: #212121;
  span {
    display: block;
    margin-top: 5px;
  }
}
</style>

<i18n lang="yml" locale="ru">
add: Добавить карту
trialTextTitle: Пожалуйста, введите данные своей карты, чтобы активировать бесплатный 30-дневный пробный период для тарифного плана «{plan}».
trialTextSubtitle: {
  '1': 'По окончании пробного периода с вашей карты будет автоматически списана сумма в размере {price} {currency} в течение следующего месяца для оплаты тарифного плана «{plan}».',
  '2': 'Вы можете отменить платеж в соответствии с планом в любое время.',
  '3': 'При оплате за год вперед ежемесячная плата составит {price}'
}
</i18n>
<i18n lang="yml" locale="en">
add: Add card
trialTextTitle: Please enter your card details to activate the free 30-day trial period for the «{plan}» plan.
trialTextSubtitle: {
  '1': 'At the end of the trial period, an amount of {price} {currency} will be automatically charged to your card for the next month to pay for the «{plan}» plan.',
  '2': 'You can cancel the plan payment at any time.',
  '3': 'By paying for a year in advance, the monthly fee will be {price}'
}
</i18n>
